import type { ThemePreference } from "@deca/shared-types";

/**
 * Resolución del tema — lógica pura y comprobable, separada del proveedor de
 * React para poder testearla sin DOM.
 *
 * Tres estados (Claro / Oscuro / Sistema). "Sistema" no se guarda como
 * resultado: se resuelve contra la preferencia del dispositivo en cada
 * momento, para que un cambio de tema del sistema operativo se refleje sin
 * tener que volver a entrar.
 */

export const THEME_STORAGE_KEY = "deca.theme";

export function isThemePreference(value: unknown): value is ThemePreference {
  return value === "LIGHT" || value === "DARK" || value === "SYSTEM";
}

export function resolveTheme(
  preference: ThemePreference,
  systemPrefersDark: boolean,
): "light" | "dark" {
  if (preference === "LIGHT") return "light";
  if (preference === "DARK") return "dark";
  return systemPrefersDark ? "dark" : "light";
}

/** Ciclo del selector de un solo botón: Claro → Oscuro → Sistema → Claro. */
export function nextThemePreference(current: ThemePreference): ThemePreference {
  if (current === "LIGHT") return "DARK";
  if (current === "DARK") return "SYSTEM";
  return "LIGHT";
}

export const THEME_LABELS: Record<ThemePreference, string> = {
  LIGHT: "Claro",
  DARK: "Oscuro",
  SYSTEM: "Sistema",
};

/** Lee la preferencia cacheada; la autoritativa vive en `User.themePreference`. */
export function readCachedTheme(): ThemePreference {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return isThemePreference(stored) ? stored : "SYSTEM";
  } catch {
    // Modo privado o almacenamiento bloqueado: no es un error que deba
    // impedir usar el panel.
    return "SYSTEM";
  }
}

export function cacheTheme(preference: ThemePreference): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    /* sin persistencia local; la preferencia sigue viva en la BD */
  }
}

/** Aplica el tema al `<html>`: Tailwind está en `darkMode: "class"`. */
export function applyThemeToDocument(theme: "light" | "dark"): void {
  document.documentElement.classList.toggle("dark", theme === "dark");
}
